import { patientDetailsRoutes } from '@/pages/patient-details/routes';
import BreadcrumbLink from '@/shared/ui/components/BreadcrumbLink';
import { Body1 } from '@/shared/ui/components/Typography';
import { Stack } from '@mui/material';
import type { UIMatch } from 'react-router-dom';
import { crumb, crumbTo } from '../../crumb';
import type { RoutePathDefinition } from '../../types';

export const patientSectionRoutes: RoutePathDefinition[] = [
  {
    path: ':id',
    children: [
      {
        path: 'details',
        lazy: patientDetailsRoutes.shell,
        handle: {
          crumb: (data: UIMatch) => {
            return <BreadcrumbLink to={data.pathname}>{data.params.id}</BreadcrumbLink>;
          }
        },
        children: [
          {
            path: '',
            Component: () => (
              <Body1
                component={Stack}
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}
              >
                No Medication Selected
              </Body1>
            )
          },
          {
            path: 'patient-history',
            handle: crumb('Patient History'),
            lazy: patientDetailsRoutes.patientHistory
          },
          {
            path: 'medication-history',
            handle: crumb('Medication History'),
            lazy: patientDetailsRoutes.medicationHistory
          },
          {
            handle: crumbTo('Medications', '/triage/list'),
            path: 'medications',
            children: [
              {
                handle: crumbTo('Tasks'),
                path: 'tasks',
                children: [
                  { path: 'list', lazy: patientDetailsRoutes.tasks.list },
                  {
                    path: ':type/:taskType',
                    lazy: patientDetailsRoutes.tasks.detail
                  },
                  {
                    path: ':type/:taskType/:taskId',
                    lazy: patientDetailsRoutes.tasks.detail
                  }
                ]
              },
              {
                handle: crumb('Triage'),
                path: 'triage',
                lazy: patientDetailsRoutes.triage.section,
                children: [
                  {
                    path: 'board',
                    lazy: patientDetailsRoutes.triage.board
                  },
                  {
                    path: 'list',
                    lazy: patientDetailsRoutes.triage.list
                  },
                  {
                    path: 'verify',
                    lazy: patientDetailsRoutes.triage.verify
                  }
                ]
              },
              { path: ':type', lazy: patientDetailsRoutes.medications.list },
              {
                path: ':type/:medicationId',
                lazy: patientDetailsRoutes.medications.list,
                handle: { crumb: (data: UIMatch) => <BreadcrumbLink>{data.params.medicationId}</BreadcrumbLink> }
              },
              {
                path: 'view/:medicationId',
                lazy: patientDetailsRoutes.medications.details
              },
              {
                path: 'encounters',
                children: [
                  {
                    path: 'view/:encounterId',
                    lazy: patientDetailsRoutes.medications.encounterDetails,
                    handle: crumb('Encounters')
                  }
                ]
              },
              {
                path: 'med-rec/:medRecId',
                lazy: patientDetailsRoutes.medications.medRecHistory,
                handle: crumb('MedRec History')
              }
            ]
          },
          {
            path: 'tcm-tasks/list',
            handle: crumb('TCM Tasks'),
            lazy: patientDetailsRoutes.tcmTasks
          },
          {
            path: 'adt-events/view/:eventId',
            handle: crumb('ADT Events'),
            lazy: patientDetailsRoutes.imports.dischargeSummary
          },
          {
            path: 'adt-events/event-details/view/:eventId',
            handle: crumb('ADT Event Details'),
            lazy: patientDetailsRoutes.imports.adtEventDetails
          },
          {
            path: 'imports/view/:eventId',
            handle: crumb('Imports'),
            lazy: patientDetailsRoutes.imports.pioneer
          },
          {
            path: 'upload-file',
            handle: crumb('Upload File'),
            lazy: patientDetailsRoutes.imports.uploadFile
          },
          {
            path: 'direct-import',
            handle: crumb('Direct Import'),
            lazy: patientDetailsRoutes.imports.directImport
          },
          {
            path: 'import-preview',
            handle: crumb('Import Preview'),
            lazy: patientDetailsRoutes.imports.preview
          },
          {
            path: 'rpm',
            handle: crumbTo('Care Management', '/readings'),
            children: [
              {
                path: 'readings',
                lazy: patientDetailsRoutes.rpm.readings,
                handle: crumb('Readings')
              },
              {
                path: 'patient-setup',
                handle: crumbTo('Patient Setup', '/care-activity'),
                lazy: patientDetailsRoutes.rpm.workflow,
                children: [
                  {
                    path: 'care-activity',
                    handle: crumbTo('Care Activity', '/clinical-summary'),
                    children: [
                      {
                        path: 'clinical-summary',
                        handle: crumb('Clinical Summary'),
                        lazy: patientDetailsRoutes.rpm.clinicalSummary
                      },
                      {
                        path: 'medication-reconciliation',
                        handle: crumb('Medication Reconciliation'),
                        lazy: patientDetailsRoutes.rpm.medReconciliation
                      },
                      {
                        path: 'configuration',
                        handle: crumb('Configuration'),
                        lazy: patientDetailsRoutes.rpm.configuration
                      }
                    ]
                  },
                  {
                    path: 'encounter',
                    handle: crumbTo('Encounter', '/consent-management'),
                    children: [
                      {
                        path: 'consent-management',
                        handle: crumb('Administration'),
                        lazy: patientDetailsRoutes.rpm.consentManagement
                      },
                      {
                        path: 'care-plan',
                        handle: crumb('Care Plan'),
                        lazy: patientDetailsRoutes.rpm.carePlan
                      },
                      {
                        path: 'devices',
                        handle: crumb('Devices'),
                        lazy: patientDetailsRoutes.rpm.devices
                      },
                      {
                        path: 'medical-consults',
                        handle: crumb('Intake (HPI)'),
                        lazy: patientDetailsRoutes.rpm.medConsults
                      }
                    ]
                  }
                ]
              },
              {
                handle: crumbTo('RPM Follow-up Consult', '/consent-management'),
                path: 'rpm-followup-consult/:rpmFollowupConsultId',
                lazy: patientDetailsRoutes.rpmFollowup.section,
                children: [
                  {
                    path: 'consent-management',
                    handle: crumb('Administration'),
                    lazy: patientDetailsRoutes.rpmFollowup.consentManagement
                  },
                  {
                    path: 'clinical-summary',
                    handle: crumb('Medical History'),
                    lazy: patientDetailsRoutes.rpmFollowup.clinicalSummary
                  },
                  {
                    path: 'medical-consults',
                    handle: crumb('Intake (HPI)'),
                    lazy: patientDetailsRoutes.rpm.medConsults
                  },
                  {
                    path: 'medication-reconciliation',
                    handle: crumb('Medication Reconciliation'),
                    lazy: patientDetailsRoutes.rpm.medReconciliation
                  },
                  {
                    path: 'care-plan',
                    handle: crumb('Care Plan'),
                    lazy: patientDetailsRoutes.rpmFollowup.carePlan
                  }
                ]
              },
              {
                path: 'activities',
                lazy: patientDetailsRoutes.rpm.activityLogs,
                handle: crumb('Activity Logs')
              },
              {
                path: 'alerts',
                lazy: patientDetailsRoutes.rpm.alerts
              },
              {
                path: 'reports',
                handle: crumb('Reports'),
                lazy: patientDetailsRoutes.rpm.reports
              },
              {
                handle: crumbTo('Clinical Care Encounter', '/clinical-summary'),
                path: 'clinical-care-encounter/:clinicalCareEncounterId',
                lazy: patientDetailsRoutes.clinicalCare.section,
                children: [
                  {
                    path: 'clinical-summary',
                    handle: crumb('Medical History'),
                    lazy: patientDetailsRoutes.clinicalCare.clinicalSummary
                  },
                  {
                    path: 'consent-management',
                    handle: crumb('Administration'),
                    lazy: patientDetailsRoutes.clinicalCare.consentManagement
                  },
                  {
                    path: 'medication-reconciliation',
                    handle: crumb('Medication Reconciliation'),
                    lazy: patientDetailsRoutes.rpm.medReconciliation
                  },
                  {
                    path: 'care-plan',
                    handle: crumb('Care Plan'),
                    lazy: patientDetailsRoutes.clinicalCare.carePlan
                  }
                ]
              }
            ]
          },
          {
            path: 'conversations',
            handle: crumb('Conversations'),
            lazy: patientDetailsRoutes.conversations
          },
          {
            path: 'notes',
            handle: crumb('Notes'),
            lazy: patientDetailsRoutes.notes
          },
          {
            path: 'pioneerrx',
            handle: crumb('Pioneer RX'),
            lazy: patientDetailsRoutes.imports.pioneerObjects
          },
          {
            path: 'follow-ups',
            handle: crumb('Follow-ups'),
            lazy: patientDetailsRoutes.followUps
          },
          {
            path: 'shared',
            children: [
              {
                path: 'health-app/medications',
                lazy: patientDetailsRoutes.healthApp.medications,
                handle: crumb('Health App Medications')
              },
              {
                path: 'health-app/immunizations',
                lazy: patientDetailsRoutes.healthApp.immunizations,
                handle: crumb('Health App Immunizations')
              },
              {
                path: 'health-app/energy',
                lazy: patientDetailsRoutes.healthApp.energy,
                handle: crumb('Energy')
              },
              {
                path: 'health-app/allergies',
                lazy: patientDetailsRoutes.healthApp.allergies,
                handle: crumb('Allergies')
              },
              {
                path: 'health-app/vitals',
                lazy: patientDetailsRoutes.healthApp.vitals,
                handle: crumb('Vitals')
              },

              {
                path: 'quickupload-history',
                lazy: patientDetailsRoutes.quickUpload.history,
                handle: crumb('Quick Upload History')
              },
              {
                path: 'quickupload-history/:sessionId',
                lazy: patientDetailsRoutes.quickUpload.items,
                handle: { crumb: (data: UIMatch) => <BreadcrumbLink>{data.params.sessionId}</BreadcrumbLink> }
              }
            ]
          },
          {
            path: 'athena',
            handle: crumb('Demographic Details'),
            lazy: patientDetailsRoutes.imports.athena
          },
          {
            path: 'patient-documents',
            handle: crumb('Account Documents'),
            lazy: patientDetailsRoutes.accountDocuments
          }
        ]
      },
      {
        path: 'copilot',
        lazy: () => import('@/shared/ui/components/CoPilotWindow/StandAloneCopilotWindow').then(m => ({ Component: m.default }))
      }
    ]
  },
  {
    path: 'tagged',
    lazy: () => import('@/pages/patients/ui/Tags/TaggedPatientsPage').then(m => ({ Component: m.default })),
    handle: crumb('Tagged Patients')
  },
  { path: '', lazy: () => import('@/pages/patients').then(m => ({ Component: m.Patients })), handle: { crumb: () => null } },
  {
    path: 'list',
    lazy: () => import('@/pages/patients/ui/PatientsList').then(m => ({ Component: m.default })),
    handle: crumb('Patients List')
  },
  {
    path: 'imported-list',
    lazy: () => import('@/pages/patients/ui/PatientsListWithImportedData').then(m => ({ Component: m.default })),
    handle: crumb('Imported Patients List')
  },
  {
    path: 'insights',
    lazy: () => import('@/pages/patients').then(m => ({ Component: m.PatientInsights })),
    handle: crumb('Patient Insights')
  },
  {
    path: 'conversations',
    lazy: () => import('@/pages/patients/ui/AllPatientsConversations').then(m => ({ Component: m.default })),
    handle: crumb('All Conversations')
  },
  {
    path: 'med-history-requests',
    lazy: () => import('@/pages/patients/ui/MedHistoryRequests').then(m => ({ Component: m.default })),
    handle: crumb('Med History Requests')
  },
  {
    path: 'patients-to-followup',
    lazy: () => import('@/pages/patients/ui/PatientsToFollowup').then(m => ({ Component: m.default })),
    handle: crumb('Patients to Follow Up')
  },
  {
    path: 'lookup',
    lazy: () => import('@/pages/patients/ui/lookup').then(m => ({ Component: m.default })),
    handle: crumb('Lookup')
  }
];
